import { useContext, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import yup from 'utils/yup';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { AgentRegistrationProfileInfoQuery } from 'interfaceAdapter/queries/AgentRegistrationProfileInfo';
import { AgentEditRepository } from 'interfaceAdapter/repositories/AgentEdit';
import { agentUserContext } from 'application/contexts/useAgentUser';
import { isNotRegistrationComplete } from 'domain/entities/AgentUser/AgentUser';
import { IIndustry } from 'domain/entities/Industry/Industry';
import { useLoading } from 'application/contexts/useLoading';

const schema = yup
    .object({
        agent_type: yup.string().required(),
        name: yup.string().required(),
        company_code: yup
            .string()
            .nullable()
            .matches(/^[0-9]+$/, '半角数字のみ入力可能です'),
        regions_served: yup.array().of(yup.string().required()).required().default([]),
        industries_served: yup.array().of(yup.string().required()).required().default([]),
        number_of_employees: yup.string().required(),
        icon_image: yup.string().nullable(),
        business_description: yup.string().required(),
        hp_url: yup.string().nullable(),
        billing_address: yup.string().required(),
        billing_zipcode: yup.string().required(),
        billing_name: yup.string().required(),
        billing_phone: yup.string().required().max(30).onlyNumber(),
        billing_email: yup.string().email().required(),
        private: yup.boolean().required().default(false),
        is_sole_proprietor: yup.boolean().required().default(false),
    })
    .required();

type AgentForm = yup.InferType<typeof schema>;

export const useCompanyEdit = () => {
    const diContainer = useContext(diContainerContext);
    const agentRegistrationProfileInfoQuery = diContainer.resolve(AgentRegistrationProfileInfoQuery);
    const agentEditRepository = diContainer.resolve(AgentEditRepository);
    const { loading, setLoading } = useLoading();
    const [areaChoices, setAreaChoices] = useState<{ value: string; label: string }[]>([]);
    const [employeeChoices, setEmployeeChoices] = useState<string[][]>([]);
    const [revenueChoices, setRevenueChoices] = useState<string[][]>([]);
    const [industryChoices, setIndustryChoices] = useState<{ value: string; label: string }[]>([]);
    const [agentTypeChoices, setAgentTypeChoices] = useState<{ value: string; label: string }[]>([]);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const { user } = useContext(agentUserContext);
    const notRegistrationComplete = isNotRegistrationComplete(user);
    const [editing, setEditing] = useState(notRegistrationComplete);

    const {
        control,
        handleSubmit,
        formState: { errors },
        getValues,
        watch,
        setValue,
    } = useForm<AgentForm>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: {
            name: '',
        },
    });
    useEffect(() => {
        const fetchData = async () => {
            const data = await agentRegistrationProfileInfoQuery.fetch();
            setAreaChoices((data.area_choices as [string, string][]).map(([value, label]) => ({ value, label })));
            setEmployeeChoices(data.employee_choices);
            setRevenueChoices(data.revenue_choices);
            setIndustryChoices(
                data.industries.map((industry: IIndustry) => ({ value: industry.id.toString(), label: industry.name })),
            );
            setAgentTypeChoices(
                (data.agent_type_choices as [string, string][]).map(([value, label]) => ({ value, label })),
            );
        };
        fetchData();
    }, []);

    useEffect(() => {
        // user.agentの情報をフォームに反映
        if (!user?.agent || !areaChoices || !industryChoices) {
            return;
        }
        setValue('agent_type', user.agent.agent_type);
        setValue('name', user.agent.name);
        setValue('company_code', user.agent.company_code);
        setValue('number_of_employees', user.agent.number_of_employees || '');
        setValue('icon_image', user.agent.icon_image);
        setValue('business_description', user.agent.business_description || '');
        setValue('hp_url', user.agent.hp_url);
        setValue('billing_address', user.agent.billing_address);
        setValue('billing_zipcode', user.agent.billing_zipcode);
        setValue('billing_name', user.agent.billing_name);
        setValue('billing_phone', user.agent.billing_phone);
        setValue('billing_email', user.agent.billing_email);
        setValue('private', user.agent.private);
        setValue('is_sole_proprietor', user.agent.is_sole_proprietor);

        const regions_served_arr =
            user.agent.regions_served?.map((region) => {
                const areaChoice = areaChoices.find((area) => area.label === region);
                return areaChoice?.value || '';
            }) || [];
        setValue('regions_served', regions_served_arr);
        const industries_served_arr =
            user.agent.industries_served?.map((industry) => {
                const industryChoice = industryChoices.find((industryChoice) => industryChoice.label === industry);
                return industryChoice?.value || '';
            }) || [];
        setValue('industries_served', industries_served_arr);
    }, [user, areaChoices, industryChoices]);

    const submit = handleSubmit(async (data) => {
        setLoading(true);
        try {
            await agentEditRepository.edit({
                ...data,
            });
            setShowSnackbar(true);
            setTimeout(() => window.location.replace('/agent/dashboard/'), 1500);
        } finally {
            setLoading(false);
        }
    });

    return {
        control,
        errors,
        loading,
        showSnackbar,
        user,
        areaChoices,
        employeeChoices,
        revenueChoices,
        industryChoices,
        agentTypeChoices,
        notRegistrationComplete,
        editing,
        setEditing,
        setShowSnackbar,
        submit,
        getValues,
        setValue,
        watch,
    };
};
